import React from 'react';
import './ScenarioAvatar.css';

/**
 * ScenarioAvatar — shared avatar-with-fallback for every guest discovery
 * card (fix round 2026-08-24, Gilbert: "every scenario card always renders
 * its avatar image"). Used by `ScenarioCard` (and therefore the #1922
 * chooser's outcome list, which renders `ScenarioCard`) and `StartHereSlot`.
 *
 * Mirrors the existing scenario-persona avatar pattern already shipped on
 * the homepage gallery (`HomePageV2.jsx`'s `getInitials`/`getAvatarColor`)
 * rather than the `LearnerIdentity` pattern (design_standards.md §9) —
 * that pattern is for human LEARNERS; this is the AI persona (`ai_role`)
 * portrait, a different identity axis. Graceful fallback for a scenario
 * with no `avatar_url` yet: a deterministic-hash colour + up-to-2-letter
 * initials monogram, never a blank box.
 */
export function getInitials(name) {
  if (!name) return '?';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return (words[0] || '').substring(0, 2).toUpperCase() || '?';
}

const AVATAR_COLORS = [
  '#667eea', '#764ba2', '#2563eb', '#0891b2',
  '#059669', '#d97706', '#dc2626', '#db2777',
];

export function getAvatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

/**
 * @param {string|null|undefined} avatarUrl - `card.avatar_url` from the
 *   discovery API (`toDiscoveryCard`)
 * @param {string} name - the persona label to derive initials/colour from
 *   when there's no image; callers pass `card.ai_role || card.name`
 * @param {'md'|'lg'} [size]
 */
function ScenarioAvatar({ avatarUrl, name, size = 'md', className = '' }) {
  const label = name || 'Scenario';
  const sizeClass = `gd-scenario-avatar--${size}`;

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={label}
        className={`gd-scenario-avatar ${sizeClass} ${className}`.trim()}
      />
    );
  }
  return (
    <div
      className={`gd-scenario-avatar ${sizeClass} gd-scenario-avatar--placeholder ${className}`.trim()}
      style={{ backgroundColor: getAvatarColor(label) }}
      aria-hidden="true"
    >
      {getInitials(label)}
    </div>
  );
}

export default ScenarioAvatar;
